<template>
  <div
    :class="[bem.b(), bem.is(isGray ? 'gray-input-password' : '')]"
    v-bind="containerAttrs"
  >
    <a-input-password
      v-bind="componentAttrs"
      ref="inputPasswordRef"
      v-model:value="newValue"
      :visible="trigger === 'hold' ? holdVisibleValue : clickVisibleValue"
      :class="bem.e('input')"
      :maxlength="newMaxlength"
      @update:visible="(v) => (clickVisibleValue = v)"
    >
      <template
        v-for="item in Object.keys($slots).filter(
          (key) => key !== 'iconRender'
        )"
        :key="item"
        #[item]="data"
      >
        <slot :name="item" v-bind="data || {}"></slot>
      </template>

      <template #iconRender>
        <!-- 修改图标容器，添加鼠标事件 -->
        <span
          v-if="trigger === 'hold'"
          :class="bem.e('icon-wrapper')"
          @mousedown.stop="handleMouseDown"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseUp"
          @touchstart.stop="handleMouseDown"
          @touchend="handleMouseUp"
          @click.stop="handleMouseUp"
        >
          <ll-icon
            :icon-name="holdVisibleValue ? 'icon-pwd-look' : 'icon-pwd-hide'"
          />
        </span>

        <ll-icon
          v-else
          :icon-name="clickVisibleValue ? 'icon-pwd-look' : 'icon-pwd-hide'"
        />
      </template>
    </a-input-password>

    <!-- 进度条 -->
    <div v-if="pwdStrength && rules.length > 0" :class="bem.e('strength')">
      <a-progress
        :class="bem.m('progress')"
        :percent="percent"
        :show-info="false"
        :stroke-color="color"
      />
      <ll-tooltip is-white-bg :overlay-class-name="bem.b('tooltip')">
        <template #title>
          <div class="title">{{ tipTitle }}</div>
          <template v-for="(item, idx) in rules" :key="idx">
            <div
              class="rule-item"
              :style="{
                color: checkResults[idx]
                  ? 'var(--color-success)'
                  : 'var(--color-text-tertiary)',
              }"
            >
              <ll-icon
                :icon-name="checkResults[idx] ? 'icon-check' : 'icon-close2'"
                :size="14"
              />
              {{ item.label }}
            </div>
          </template>
        </template>
      </ll-tooltip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useAttrs, watch } from 'vue'

import { createNamespace } from '@ll-plus/utils'
import { omit } from 'lodash-es'
import { passwordInputEmits, passwordInputProps } from './input'

defineOptions({ name: 'LlPasswordInput', inheritAttrs: false })

const props = defineProps(passwordInputProps)
const emits = defineEmits(passwordInputEmits)

const bem = createNamespace('password-input')

const attrs = useAttrs()

const inputPasswordRef = ref()

const clickVisible = ref(false) // 点击切换时的显示状态
const holdVisible = ref(false) // 按住时临时显示状态

// 容器样式
const containerAttrs = computed(() => ({
  class: attrs.class as any,
  style: attrs.style as any,
}))

// 组件功能相关的属性
const componentAttrs = computed(() => omit(attrs, ['class', 'style']))

// 使用计算属性实现双向绑定
const newValue = computed({
  get() {
    return props.value
  },
  set(value) {
    emits('update:value', value)
    emits('change', value)
  },
})

// 按住显示状态值
const holdVisibleValue = computed({
  get() {
    return props.visible || holdVisible.value
  },
  set(value) {
    holdVisible.value = value
    emits('update:visible', value)
  },
})

// 点击切换显示状态值
const clickVisibleValue = computed({
  get() {
    return props.visible || clickVisible.value
  },
  set(value) {
    clickVisible.value = value
    emits('update:visible', value)
  },
})

// 最大输入限制
const newMaxlength = computed(() => {
  // 处理 maxlength 最大长度为 0 则不限制
  if (typeof props.maxlength === 'number') {
    return props.maxlength <= 0 ? null : props.maxlength
  }
  return props.maxlength
})

const checkResults = computed(() => {
  return props.rules.map((rule) => {
    // 去除正则表达式字符串首尾的引号（如果存在）
    const regexPattern = rule.regex.replace(/^['"]|['"]$/g, '')
    // 创建正则表达式对象并测试值
    const regex = new RegExp(regexPattern)
    return regex.test(props.value)
  })
})

// 进度条
const percent = computed(() => {
  return (100 / props.rules.length) * checkResults.value.filter(Boolean).length
})

// 进度条颜色
const color = computed(() => {
  if (percent.value < 50) {
    return props.weakColor
  } else if (percent.value < 100) {
    return props.mediumColor
  } else {
    return props.strongColor
  }
})

const focus = () => {
  inputPasswordRef.value.focus()
}
const select = () => {
  inputPasswordRef.value.select()
}

// 鼠标按下事件
const handleMouseDown = () => {
  holdVisible.value = true
}

// 鼠标松开事件
const handleMouseUp = () => {
  holdVisible.value = false
}

watch(
  () => percent.value,
  (v) => {
    emits('state', v === 100)
  },
  {
    immediate: true,
  }
)

watch(
  () => props.trigger,
  (v) => {
    if (v === 'hold') {
      holdVisible.value = false
    }
  },
  {
    immediate: true,
  }
)

// 公开focus方法
defineExpose({
  focus,
  select,
})
</script>
